import React, { useState, useCallback } from 'react';
import { FileRejection } from 'react-dropzone';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
// mui components
import { Grid, Button } from '@mui/material';
// components
import FileUpload from 'components/file-cabinet-folders/components/file-upload';
import CustomModal from '../components/custom-modal';
import StorageFullAlertModal from '../storage-full-alert-modal';
import SelectController from 'controllers/select-controller';
import FileCard from 'components/file-cabinet-folders/components/file-card';
import { TModalProps } from 'components/file-cabinet-folders/types';
// hooks
import useToast from 'hooks/useToast';
import { destinationFoldersOptions } from 'utils/helpers/fileCabinetHelpers';
// api
import {
  IFileCabinetUploadURLResponse,
  useGetDestinationFoldersQuery,
  useLazyGetFileCabinetUploadURlQuery,
  useUploadFileCabinetFileMutation,
} from 'redux-store/api/file-cabinet';
// styles
import { styles } from './styles';
import axios from 'axios';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { v4 as uuid } from 'uuid';
import {
  addUpload,
  failUpload,
  finishUpload,
  updateUploadProgress,
} from 'redux-store/slices/file-cabinet-upload-slice';

type UploadFileProps = TModalProps & {
  fromModal?: boolean;
  projectFolderId?: string | number;
  projectId: string | number;
  canCreateFolders: boolean;
};

const UploadFileModal: React.FC<UploadFileProps> = ({
  open,
  fromModal,
  projectFolderId,
  projectId,
  canCreateFolders,
  handleClose,
}) => {
  const dispatch = useAppDispatch();

  const [files, setFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
  const [isOpenStorageFull, setIsOpenStorageFull] = useState(false);

  const { folderId } = useParams();
  const { hash } = useLocation();
  const showToast = useToast();

  const [postFileToCabinet] = useUploadFileCabinetFileMutation();
  const [getUploadUrl] = useLazyGetFileCabinetUploadURlQuery();
  const { data: folders, isLoading: isLoading } = useGetDestinationFoldersQuery(
    {
      projectId: projectId,
    },
    { skip: !canCreateFolders }
  );

  const form = useForm({});
  const { handleSubmit, clearErrors, reset } = form;

  const handleOpenStorageModal = (val: boolean) => setIsOpenStorageFull(val);

  const handleFileChange = (newFiles: File[]) => {
    setFiles((prev) => [...newFiles, ...prev]);
  };

  const handleRejectedFilesChange = (newFiles: FileRejection[]) =>
    setRejectedFiles((prev) => [...newFiles, ...prev]);

  const handleDeleteFile = (name: string) => {
    setFiles((prev) => prev.filter((item) => item.name !== name));
  };

  const closeModalHandler = useCallback(() => {
    handleClose();
    setFiles([]);
  }, []);

  const checkParentFolderId = useCallback(() => {
    if (!!folderId) {
      return folderId;
    }

    if (fromModal && !!hash) {
      return hash?.substring(1);
    }

    if (fromModal && !!projectFolderId) {
      return projectFolderId;
    }

    return 0;
  }, [folderId, hash, projectFolderId]);

  const uploadFileWithProgress = async ({
    file,
    AWSRes,
    projectId,
    folderId,
    tempId,
  }: {
    file: File;
    AWSRes: IFileCabinetUploadURLResponse;
    projectId: number;
    folderId: number;
    tempId: string;
  }) => {
    if (!AWSRes) {
      showToast({ type: 'error', message: 'Missing AWS upload URL' });
      return;
    }

    try {
      await axios.put(AWSRes.url, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / event.total);

          dispatch(
            updateUploadProgress({
              tempId,
              progress: percent,
            })
          );
        },
      });

      await postFileToCabinet({
        folderId: folderId ?? checkParentFolderId(),
        fileId: AWSRes.id,
        projectId,
      }).unwrap();

      dispatch(
        finishUpload({
          tempId,
        })
      );
    } catch (error) {
      dispatch(
        failUpload({
          tempId,
        })
      );

      showToast({ type: 'error', message: 'Something went wrong!' });
    }
  };

  const handleCustomSubmit = useCallback(
    async (data) => {
      if (!!files?.length) {
        await Promise.all(
          files?.map(async (file) => {
            const tempId = uuid();

            dispatch(
              addUpload({
                tempId,
                mimeType: file?.type,
                size: file?.size,
                fileName: file.name,
                progress: 0,
                status: 'uploading',
                projectId: Number(projectId),
                folderId: data?.folderId ?? checkParentFolderId(),
              })
            );

            getUploadUrl({
              folderId: !!data?.folderId ? data?.folderId : checkParentFolderId(),
              key: file.name,
              mimeType: file?.type,
            })
              .unwrap()
              .then(async (AWSRes) => {
                const formData = new FormData();
                formData.append('file', file);

                await uploadFileWithProgress({
                  AWSRes,
                  file,
                  projectId: Number(projectId),
                  folderId: data?.folderId,
                  tempId,
                });
              })
              .catch(() => {
                dispatch(
                  failUpload({
                    tempId,
                  })
                );
                showToast({ type: 'error', message: 'Something went wrong!' });
              });
          })
        ).then(() => {
          handleClose();
          clearErrors();
          setFiles([]);
          reset();
        });
      }
    },
    [files]
  );

  return (
    <>
      <CustomModal
        open={open}
        handleClose={closeModalHandler}
        title="Upload File"
        subtitle="Here you can upload files to the folder"
      >
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(handleCustomSubmit)}>
            {canCreateFolders && (
              <SelectController
                name="folderId"
                label="Folder"
                options={destinationFoldersOptions(folders?.items)}
                defaultValue={hash?.substring(1)}
                disabled={isLoading}
                isSelect
                paperStyles={{}}
                withError
              />
            )}
            <FileUpload
              files={files}
              rejectedFiles={rejectedFiles}
              handleFileChange={handleFileChange}
              handleRejectedFilesChange={handleRejectedFilesChange}
            />
            {files?.map((file) => (
              <FileCard
                key={file?.name}
                uploadedFile={file}
                handleDeleteUploadedFile={handleDeleteFile}
              />
            ))}
            {rejectedFiles?.map((item) => (
              <FileCard
                key={item?.file?.name}
                rejectedFile={item}
                handleDeleteUploadedFile={handleDeleteFile}
              />
            ))}

            <Grid sx={styles.actions}>
              <Button className="btn" onClick={closeModalHandler}>
                Cancel
              </Button>
              <Button className="btn filled-btn" type="submit" disabled={!files?.length}>
                Save
              </Button>
            </Grid>
          </form>
        </FormProvider>
      </CustomModal>

      <StorageFullAlertModal
        open={isOpenStorageFull}
        handleClose={() => handleOpenStorageModal(false)}
        currentCapacity={4.8}
        maxCapacity={5}
      />
    </>
  );
};

export default UploadFileModal;
